import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import {
	ClientPortfolioDataCommodityServiceEnum,
	type ServiceDetailDataDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailCard } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailCard";
import { DetailLoader } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailLoader";
import { DetailRow } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailRow";
import { RelatedSectionRedesign } from "web_myplann/Portfolio/Components/Redesign/Detail/RelatedSectionRedesign";
import { PortfolioBasicInfoRows } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/PortfolioBasicInfoRows";
import { ServicesBasicInfoDetails } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/ServicesBasicInfoDetails";
import { formatEnumLabel } from "web_myplann/Portfolio/Helpers/formatEnumLabel";
import { getPortfolioFrequencyValue } from "web_myplann/Portfolio/Helpers/portfolioDetailFormatters";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";
import { hasAnyDetailValue } from "web_myplann/Shared/Utils/hasAnyDetailValue";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";

const PageResources = Resources.PortfolioDetail.Services;

export const ServicesRedesign: FC<PortfolioDetailProps> = ({
	baseData,
	currencySign,
}) => {
	const { t } = useResource();

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);

	if (isDocumentsLoading) {
		return <DetailLoader />;
	}

	const portfolioData = baseData.data as ServiceDetailDataDTO;

	const commodity = formatEnumLabel(
		portfolioData.commodityService,
		ClientPortfolioDataCommodityServiceEnum.None,
		PageResources.CommodityService,
		t,
	);

	const depositAmount = portfolioData?.depositAmount
		? getPortfolioFrequencyValue(
				portfolioData?.depositAmount,
				currencySign,
				portfolioData.paymentFrequency ?? null,
				t,
			)
		: EMPTY_DETAIL_FIELD;

	const fixedPrice = portfolioData?.fixedPriceByFrequency
		? getPortfolioFrequencyValue(
				portfolioData?.fixedPriceByFrequency,
				currencySign,
				portfolioData.paymentFrequency ?? null,
				t,
			)
		: EMPTY_DETAIL_FIELD;

	const tariff = portfolioData?.tariff;
	const pricePerUnit = moneyFormatter(portfolioData.pricePerUnit, currencySign);

	return (
		<>
			<DetailCard label={t(Resources.DataCard.BasicInfo.title)}>
				<PortfolioBasicInfoRows baseData={baseData} />
			</DetailCard>
			<ServicesBasicInfoDetails
				baseData={baseData}
				depositAmount={depositAmount}
			/>

			{hasAnyDetailValue(tariff, commodity, pricePerUnit, fixedPrice) && (
				<DetailCard label={t(PageResources.cardLabel)}>
					<DetailRow label={t(PageResources.tariff)} value={tariff} />
					<DetailRow label={t(PageResources.commodity)} value={commodity} />
					<DetailRow
						label={
							portfolioData?.unitOfMeasurement?.trim()
								? t(PageResources.pricePerUnit, {
										unit: portfolioData.unitOfMeasurement,
									})
								: t(PageResources.pricePerUnitWithoutUnit)
						}
						value={pricePerUnit}
					/>
					<DetailRow label={t(PageResources.fixedPrice)} value={fixedPrice} />
				</DetailCard>
			)}

			<RelatedSectionRedesign
				documents={documents?.data}
				related={baseData.related}
				itemKind={PortfolioItemKind.Portfolio}
				portfolioId={baseData.id}
			/>
		</>
	);
};
